// ---------------------------------------------------------------------------
// Locale tables for the report workspace (the IDE rail's "Status" item).
//
// WHY A CO-LOCATED MODULE. The 2026-08-18 Studio i18n census measured 131
// operator-facing English literals in `report-workspace-surface.tsx` — the
// largest single file in the document+report lane — plus ~40 more produced by
// `report-workspace-model.ts` and rendered verbatim by that surface. Routing
// them through `src/i18n/studio-dictionaries.ts` would grow the shared
// dictionary for one screen and force every parallel i18n lane through the
// same file. The house pattern for a surface this size is a co-located
// `*-copy.ts` keyed by `LocaleCode` (`evidence-surface-copy.ts`,
// `rail-surface-copy.ts`, `publish-gate-copy.ts`, `release-journey-copy.ts`,
// and this directory's own `report-upload-store-copy.ts` all do this).
//
// WHY THE MODEL SHARES THIS TABLE. `buildReportWorkspaceModel` composes the
// lane labels, lane details, page labels and blocker sentences that the
// surface renders without further processing. Localizing the surface alone
// would leave English inside the model's output, which is exactly the
// "repair scoped by whatever the person happened to read" failure the census
// documented. The builder therefore takes a `locale` option (defaulting to
// `en`, so existing callers and tests are unaffected) and reads this table.
//
// WHAT IS DELIBERATELY NOT IN HERE — assert, do not translate:
//   - Export FORMAT tokens (`pdf` `docx` `pptx` `hwpx`). File-format
//     identifiers; the operator matches them against the artifact they
//     downloaded. Exported as `REPORT_EXPORT_TARGETS` from the model and
//     asserted untranslated by the i18n gate.
//   - Production-input MODE tokens (`platform-upload` `embedded-evidence`
//     `fixture` `missing`) and analysis-status tokens. Schema vocabulary
//     carried in the evidence payload — the same boundary `data-inspector.tsx`
//     documents for element types. Exported as
//     `REPORT_PRODUCTION_INPUT_MODES` for the gate to assert.
//   - Evidence-item KIND tokens (`artifact-readback`, `native-detail`,
//     `visual-residual`, `chart-readback`, `semantic-readback`) and evidence
//     refs / paths / SHA prefixes / sheet names / chart anchors. Payload, not
//     copy.
//   - The acronyms carried inside otherwise-translated field labels
//     (`SHA`, `MIME`, `URL`, `A4`, `Coogo`, `Univer`, `ActionFlow`).
//   - `downloadReportWorkspaceExportArtifact`'s `note:` field. That string is
//     written into the exported JSON packet, not onto a screen; it is machine
//     provenance and stays in one language on purpose.
//
// Status words (`Ready` / `Warning` / `Blocked` / `Not yet`) are NOT here
// either — they already live at `strings.report.status` in the shared
// dictionary and are shared with the ontology report surface. Duplicating
// them here would create two tables that can drift.
// ---------------------------------------------------------------------------

import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

/** Field-label and heading copy for the export-view inspector. */
export interface ReportWorkspaceSurfaceCopy {
  /** Toolbar + navigator framing. */
  workspaceEyebrow: string;
  reviewReport: string;
  paneSwitcherLabel: string;
  navigate: string;
  preview: string;
  navigatorLabel: string;
  /** `12 pages` in the toolbar subtitle and the document-pages counter. */
  pagesCount: (count: number) => string;
  /** `3 nodes` under a page button. */
  nodesCount: (count: number) => string;
  /** `2 series` under a chart button. */
  seriesCount: (count: number) => string;

  /** A4 preview chrome. */
  a4Portrait: string;
  coogoReport: string;
  /** `Page 2 of 9` in the preview header. */
  pageOfPages: (label: string, total: number) => string;
  noPages: string;
  laneSource: string;
  lanePreview: string;
  laneExport: string;
  pageEvidence: string;
  attachPageEvidence: string;
  noPageEvidenceTitle: string;
  noPageEvidenceBody: string;

  /** Navigator metrics. */
  metricPages: string;
  metricNodes: string;
  metricReady: string;
  notAReportProject: string;

  /** Production input. */
  productionInput: string;
  factMode: string;
  factUpload: string;
  factFile: string;
  factAnalysis: string;
  factStatus: string;
  factAppSource: string;
  factCheck: string;
  upload: string;
  uploading: string;
  uploadAttached: string;
  uploadPending: string;
  uploadFailed: string;
  sourceNotReady: string;

  /** Document pages. */
  documentPages: string;
  factSelected: string;
  factDetail: string;
  factEvidence: string;
  reviewPage: string;
  editPage: string;
  validatePage: string;
  exportPage: string;
  attachPagesOrGraph: string;

  /** Export targets + readiness. */
  exportTargets: string;
  /** `PDF readiness` — the format token stays untranslated. */
  targetReadiness: (format: string) => string;
  validateTarget: string;
  checkWriter: string;
  checkReadback: string;
  checkRender: string;
  checkPages: string;
  checkParity: string;
  checkSemantic: string;
  /** The pass/blocked words rendered beside each check row. */
  outcomePass: string;
  outcomeBlocked: string;
  outcomeFail: string;
  outcomePending: string;
  factOracle: string;
  factWriterState: string;
  factArtifact: string;
  factSize: string;
  evidencePacket: string;
  notRecorded: string;

  /** Native detail. */
  nativeDetail: string;
  factComponents: string;
  factCoverage: string;
  factRules: string;
  factProfiles: string;
  factBorders: string;
  factAuthority: string;
  factPrimitive: string;
  outcomeReady: string;

  /** Visual residual. */
  visualResidual: string;
  factStrict: string;
  factFloor: string;
  factLimit: string;
  factResidual: string;
  factPage: string;
  /** `page 4` — the residual page indicator. */
  pageOrdinal: (page: number) => string;
  /** `12.00% / page 3.40%` — the shared ratio-per-page shape. */
  ratioPerPage: (total: string, perPage: string) => string;

  /** Artifact service. */
  artifactService: string;
  factArtifacts: string;
  factFormats: string;
  factRoute: string;
  missing: string;

  /** Chart semantics. */
  chartSemantics: string;
  factCharts: string;
  factFormula: string;
  factBehavior: string;
  factNative: string;
  factPreview: string;
  factSidecar: string;
  factPolicy: string;
  factPath: string;
  factSeries: string;
  /** `2/5 formula` in the chart detail. */
  seriesFormula: (linked: number, total: number) => string;
  factAction: string;
  factCatAxis: string;
  factValAxis: string;
  factLegend: string;
  factCategory: string;
  factValues: string;
  factFormat: string;
  factAxis: string;
  legendHidden: string;
  legendOverlaySuffix: string;
  none: string;
  univerHelperPreview: string;
  factAdapter: string;
  factPackage: string;
  factChart: string;
  factBindings: string;
  factReadbacks: string;

  /** Formula oracle. */
  formulaOracle: string;
  factCases: string;
  factEngine: string;
  factDeps: string;
  factGraph: string;
  factSteps: string;
  factSource: string;
  factInputs: string;
  factNamed: string;
  factBinding: string;
  factMapping: string;
  run: string;
  running: string;

  /** Native artifact + check trail. */
  nativeArtifact: string;
  download: string;
  checkTrail: string;
  open: string;

  /** Semantic edit roundtrip. */
  semanticEditRoundtrip: string;
  factOperation: string;
  factExpected: string;
  factReplacements: string;
  replacementText: string;
  apply: string;

  /**
   * Names for the three built-in formula sample cases. Keyed by the ids
   * `UNIVER_FORMULA_DEFAULT_CASE_IDS` declares; a project's own cases arrive
   * named in the evidence payload and pass through untouched.
   */
  sampleCase: Record<string, string>;

  /** Footer + tail sections. */
  readinessLanes: string;
  exportHistory: string;
  exportBlocked: string;
  recheck: string;
  /** `Export PDF` — the format token stays untranslated. */
  exportFormat: (format: string) => string;
}

/** Prose `buildReportWorkspaceModel` composes and the surface renders as-is. */
export interface ReportWorkspaceModelCopy {
  fallbackTitle: string;
  pageLabel: (pageIndex: number) => string;
  pageDetailNotAttached: string;
  countNodes: (count: number) => string;
  countParagraphs: (count: number) => string;
  countTables: (count: number) => string;
  countPictures: (count: number) => string;
  countLayers: (count: number) => string;

  /** `PDF artifact` — the format token stays untranslated. */
  evidenceNativeArtifact: (format: string) => string;
  evidenceFileCheck: string;
  evidenceChartCheck: string;
  evidenceComponentDetail: string;
  evidenceVisualResidual: string;
  evidenceContentCheck: string;
  detailServiceArtifact: string;
  detailCheckPassed: string;
  detailCheckBlocked: string;
  detailChartProof: string;
  detailNativeSemanticPass: string;
  detailMissing: string;

  laneProductionInput: string;
  laneSourceCorpus: string;
  laneProductionExport: string;
  laneSemanticRoundtrip: string;
  laneArtifactService: string;
  laneFormulaOracle: string;
  laneChartSemantics: string;
  lanePublishReadiness: string;

  laneDetailAnalyzedBy: (fileName: string, jobId: string) => string;
  laneDetailSourceLoaded: (sourceKind: string) => string;
  laneDetailSourceMissing: string;
  laneDetailTargetsReady: (ready: number, total: number) => string;
  laneDetailSemanticPass: string;
  laneDetailSemanticFail: string;
  laneDetailServiceMissing: string;
  laneDetailArtifactsPass: (pass: number, total: number) => string;
  laneDetailCases: (pass: number, total: number, engine: string) => string;
  laneDetailCharts: (charts: number, series: number, mode: string) => string;
  laneDetailNoBlockers: string;
  laneDetailBlocked: string;

  /** Chart-inspector blockers. Composed in `chart-semantic-inspector.ts`
   *  and rendered verbatim by the report surface's Chart semantics section. */
  blockerChartNoSeriesBindings: string;
  blockerChartDataMissing: string;
  blockerChartDataMissingFormulaOnly: string;
  blockerChartDataNotLoaded: string;

  blockerFixturePromotion: string;
  blockerUploadIdMissing: string;
  blockerAnalysisJobIdMissing: string;
  blockerAnalysisNotReady: string;
  blockerNormalizedNotReady: string;
  blockerCommercialRequiresUpload: string;
  blockerUploadAndAnalysisMissing: string;
  blockerExportEvidenceMissing: string;
  blockerProductionGateNotReady: string;
  blockerSemanticProofNotReady: string;
  blockerArtifactServiceNotReady: string;
  blockerFormulaOracleNotReady: string;
  /** `DOCX export evidence is missing.` — format token stays untranslated. */
  blockerTargetExportMissing: (format: string) => string;
  blockerTargetVisualMissing: (format: string) => string;
  blockerTargetNativeDetailMissing: (format: string) => string;
}

export interface ReportWorkspaceCopy {
  surface: ReportWorkspaceSurfaceCopy;
  model: ReportWorkspaceModelCopy;
}

const EN: ReportWorkspaceCopy = {
  surface: {
    workspaceEyebrow: "Report workspace",
    reviewReport: "Review report",
    paneSwitcherLabel: "Report workspace pane",
    navigate: "Navigate",
    preview: "Preview",
    navigatorLabel: "Report navigator",
    pagesCount: (count) => `${count} ${count === 1 ? "page" : "pages"}`,
    nodesCount: (count) => `${count} ${count === 1 ? "node" : "nodes"}`,
    seriesCount: (count) => `${count} series`,

    a4Portrait: "A4 portrait",
    coogoReport: "Coogo report",
    pageOfPages: (label, total) => `${label} of ${total}`,
    noPages: "No pages",
    laneSource: "Source",
    lanePreview: "Preview",
    laneExport: "Export",
    pageEvidence: "Page evidence",
    attachPageEvidence:
      "Attach document page evidence to inspect this report by page.",
    noPageEvidenceTitle: "No report page evidence",
    noPageEvidenceBody:
      "Upload or attach a supported source before preview and export readiness can be evaluated.",

    metricPages: "Pages",
    metricNodes: "Nodes",
    metricReady: "Ready",
    notAReportProject: "Report evidence is not attached to this project.",

    productionInput: "Production input",
    factMode: "Mode",
    factUpload: "Upload",
    factFile: "File",
    factAnalysis: "Analysis",
    factStatus: "Status",
    factAppSource: "App source",
    factCheck: "Check",
    upload: "Upload",
    uploading: "Uploading",
    uploadAttached: "Upload evidence attached.",
    uploadPending: "Platform analysis is pending.",
    uploadFailed: "Report upload failed.",
    sourceNotReady:
      "The source is not ready for editable Coogo JSON or native export.",

    documentPages: "Document pages",
    factSelected: "Selected",
    factDetail: "Detail",
    factEvidence: "Evidence",
    reviewPage: "Review page",
    editPage: "Edit page",
    validatePage: "Validate page",
    exportPage: "Export page",
    attachPagesOrGraph:
      "Attach document model pages or a layout graph to inspect this report by page.",

    exportTargets: "Export targets",
    targetReadiness: (format) => `${format} readiness`,
    validateTarget: "Validate target",
    checkWriter: "Writer",
    checkReadback: "Readback",
    checkRender: "Render",
    checkPages: "Pages",
    checkParity: "Parity",
    checkSemantic: "Semantic",
    outcomePass: "pass",
    outcomeBlocked: "blocked",
    outcomeFail: "fail",
    outcomePending: "pending",
    factOracle: "Oracle",
    factWriterState: "Writer state",
    factArtifact: "Artifact",
    factSize: "Size",
    evidencePacket: "evidence packet",
    notRecorded: "not recorded",

    nativeDetail: "Native detail",
    factComponents: "Components",
    factCoverage: "Coverage",
    factRules: "Rules",
    factProfiles: "Profiles",
    factBorders: "Borders",
    factAuthority: "Authority",
    factPrimitive: "Primitive",
    outcomeReady: "ready",

    visualResidual: "Visual residual",
    factStrict: "Strict",
    factFloor: "Floor",
    factLimit: "Limit",
    factResidual: "Residual",
    factPage: "Page",
    pageOrdinal: (page) => `page ${page}`,
    ratioPerPage: (total, perPage) => `${total} / page ${perPage}`,

    artifactService: "Artifact service",
    factArtifacts: "Artifacts",
    factFormats: "Formats",
    factRoute: "Route",
    missing: "missing",

    chartSemantics: "Chart semantics",
    factCharts: "Charts",
    factFormula: "Formula",
    factBehavior: "Behavior",
    factNative: "Native",
    factPreview: "Preview",
    factSidecar: "Sidecar",
    factPolicy: "Policy",
    factPath: "Path",
    factSeries: "Series",
    seriesFormula: (linked, total) => `${linked}/${total} formula`,
    factAction: "Action",
    factCatAxis: "Cat axis",
    factValAxis: "Val axis",
    factLegend: "Legend",
    factCategory: "Category",
    factValues: "Values",
    factFormat: "Format",
    factAxis: "Axis",
    legendHidden: "hidden",
    legendOverlaySuffix: " overlay",
    none: "none",
    univerHelperPreview: "Univer helper preview",
    factAdapter: "Adapter",
    factPackage: "Package",
    factChart: "Chart",
    factBindings: "Bindings",
    factReadbacks: "Readbacks",

    formulaOracle: "Formula oracle",
    factCases: "Cases",
    factEngine: "Engine",
    factDeps: "Deps",
    factGraph: "Graph",
    factSteps: "Steps",
    factSource: "Source",
    factInputs: "Inputs",
    factNamed: "Named",
    factBinding: "Binding",
    factMapping: "Mapping",
    run: "Run",
    running: "Running",

    nativeArtifact: "Native artifact",
    download: "download",
    checkTrail: "Check trail",
    open: "Open",

    semanticEditRoundtrip: "Semantic edit roundtrip",
    factOperation: "Operation",
    factExpected: "Expected",
    factReplacements: "Replacements",
    replacementText: "Replacement text",
    apply: "Apply",

    sampleCase: {
      "report-total-sum": "Report total",
      "report-change-rate": "Change rate",
      "actionflow-checkpoint": "ActionFlow checkpoint",
    },
    readinessLanes: "Readiness lanes",
    exportHistory: "Export history",
    exportBlocked: "Export blocked",
    recheck: "Re-check",
    exportFormat: (format) => `Export ${format}`,
  },
  model: {
    fallbackTitle: "Document report",
    pageLabel: (pageIndex) => `Page ${pageIndex}`,
    pageDetailNotAttached: "Page-level layout evidence not attached yet.",
    countNodes: (count) => `${count} ${count === 1 ? "node" : "nodes"}`,
    countParagraphs: (count) =>
      `${count} ${count === 1 ? "paragraph" : "paragraphs"}`,
    countTables: (count) => `${count} ${count === 1 ? "table" : "tables"}`,
    countPictures: (count) =>
      `${count} ${count === 1 ? "picture" : "pictures"}`,
    countLayers: (count) => `${count} ${count === 1 ? "layer" : "layers"}`,

    evidenceNativeArtifact: (format) => `${format} artifact`,
    evidenceFileCheck: "File check",
    evidenceChartCheck: "Chart check",
    evidenceComponentDetail: "Component detail",
    evidenceVisualResidual: "Visual residual",
    evidenceContentCheck: "Content check",
    detailServiceArtifact: "service artifact",
    detailCheckPassed: "check passed",
    detailCheckBlocked: "check blocked",
    detailChartProof: "chart proof",
    detailNativeSemanticPass: "native semantic pass",
    detailMissing: "missing",

    laneProductionInput: "Production input",
    laneSourceCorpus: "Source corpus",
    laneProductionExport: "Production export",
    laneSemanticRoundtrip: "Semantic roundtrip",
    laneArtifactService: "Artifact service",
    laneFormulaOracle: "Formula oracle",
    laneChartSemantics: "Chart semantics",
    lanePublishReadiness: "Publish readiness",

    laneDetailAnalyzedBy: (fileName, jobId) =>
      `${fileName} analyzed by ${jobId}`,
    laneDetailSourceLoaded: (sourceKind) => `${sourceKind} source model loaded`,
    laneDetailSourceMissing: "No document source corpus evidence attached",
    laneDetailTargetsReady: (ready, total) => `${ready}/${total} targets ready`,
    laneDetailSemanticPass:
      "Coogo JSON semantic edit reads back from native artifacts",
    laneDetailSemanticFail:
      "The content check for semantic edits is incomplete",
    laneDetailServiceMissing:
      "Native artifact service manifest is not attached",
    laneDetailArtifactsPass: (pass, total) =>
      `${pass}/${total} native artifacts pass`,
    laneDetailCases: (pass, total, engine) =>
      `${pass}/${total} cases · ${engine}`,
    laneDetailCharts: (charts, series, mode) =>
      `${charts}/${series} charts/series · ${mode}`,
    laneDetailNoBlockers: "No report export blockers",
    laneDetailBlocked: "Blocked",

    blockerChartNoSeriesBindings:
      "Chart data has charts but no series bindings.",
    blockerChartDataMissing: "Chart data is missing.",
    blockerChartDataMissingFormulaOnly:
      "Chart data is missing; using formula chart series references only.",
    blockerChartDataNotLoaded: "Chart data is referenced but has not loaded.",
    blockerFixturePromotion:
      "Fixture/POC report evidence cannot be promoted as commercial upload readiness.",
    blockerUploadIdMissing: "Studio upload id is missing.",
    blockerAnalysisJobIdMissing:
      "Platform document analysis job id is missing.",
    blockerAnalysisNotReady: "Platform document analysis is not ready.",
    blockerNormalizedNotReady:
      "Normalized Coogo JSON and its saved evidence are not ready.",
    blockerCommercialRequiresUpload:
      "Commercial report readiness requires Studio upload and platform analysis evidence.",
    blockerUploadAndAnalysisMissing:
      "Report upload and platform analysis evidence are missing.",
    blockerExportEvidenceMissing: "Document/report export evidence is missing.",
    blockerProductionGateNotReady:
      "Document export production gate is not ready.",
    blockerSemanticProofNotReady: "Semantic edit roundtrip proof is not ready.",
    blockerArtifactServiceNotReady:
      "Document export artifact service is not ready.",
    blockerFormulaOracleNotReady: "Spreadsheet formula oracle is not ready.",
    blockerTargetExportMissing: (format) =>
      `${format} export evidence is missing.`,
    blockerTargetVisualMissing: (format) =>
      `${format} visual quality evidence is missing.`,
    blockerTargetNativeDetailMissing: (format) =>
      `${format} native detail evidence is missing.`,
  },
};

const KO: ReportWorkspaceCopy = {
  surface: {
    workspaceEyebrow: "보고서 작업 공간",
    reviewReport: "보고서 검토",
    paneSwitcherLabel: "보고서 작업 공간 화면",
    navigate: "탐색",
    preview: "미리보기",
    navigatorLabel: "보고서 탐색기",
    pagesCount: (count) => `${count}쪽`,
    nodesCount: (count) => `노드 ${count}개`,
    seriesCount: (count) => `계열 ${count}개`,

    a4Portrait: "A4 세로",
    coogoReport: "Coogo 보고서",
    pageOfPages: (label, total) => `${label} / 전체 ${total}쪽`,
    noPages: "쪽 없음",
    laneSource: "원본",
    lanePreview: "미리보기",
    laneExport: "내보내기",
    pageEvidence: "쪽 근거",
    attachPageEvidence:
      "쪽 단위로 이 보고서를 확인하려면 문서 쪽 근거를 연결하세요.",
    noPageEvidenceTitle: "보고서 쪽 근거 없음",
    noPageEvidenceBody:
      "미리보기와 내보내기 준비 상태를 평가하려면 먼저 지원되는 원본을 올리거나 연결하세요.",

    metricPages: "쪽",
    metricNodes: "노드",
    metricReady: "준비됨",
    notAReportProject: "이 프로젝트에는 보고서 근거가 연결되어 있지 않아요.",

    productionInput: "실사용 입력",
    factMode: "모드",
    factUpload: "업로드",
    factFile: "파일",
    factAnalysis: "분석",
    factStatus: "상태",
    factAppSource: "앱 원본",
    factCheck: "점검",
    upload: "올리기",
    uploading: "올리는 중",
    uploadAttached: "업로드 근거를 연결했어요.",
    uploadPending: "플랫폼 분석이 진행 중이에요.",
    uploadFailed: "보고서 업로드에 실패했어요.",
    sourceNotReady:
      "이 원본은 편집 가능한 Coogo JSON이나 네이티브 내보내기에 아직 준비되지 않았어요.",

    documentPages: "문서 쪽",
    factSelected: "선택됨",
    factDetail: "상세",
    factEvidence: "근거",
    reviewPage: "쪽 검토",
    editPage: "쪽 편집",
    validatePage: "쪽 검증",
    exportPage: "쪽 내보내기",
    attachPagesOrGraph:
      "쪽 단위로 이 보고서를 확인하려면 문서 모델 쪽이나 레이아웃 그래프를 연결하세요.",

    exportTargets: "내보내기 대상",
    targetReadiness: (format) => `${format} 준비 상태`,
    validateTarget: "대상 검증",
    checkWriter: "작성기",
    checkReadback: "역판독",
    checkRender: "렌더링",
    checkPages: "쪽 수",
    checkParity: "동등성",
    checkSemantic: "의미",
    outcomePass: "통과",
    outcomeBlocked: "차단됨",
    outcomeFail: "실패",
    outcomePending: "대기 중",
    factOracle: "오라클",
    factWriterState: "작성기 상태",
    factArtifact: "산출물",
    factSize: "크기",
    evidencePacket: "근거 묶음",
    notRecorded: "기록 없음",

    nativeDetail: "네이티브 상세",
    factComponents: "구성요소",
    factCoverage: "적용 범위",
    factRules: "규칙",
    factProfiles: "프로파일",
    factBorders: "테두리",
    factAuthority: "권위",
    factPrimitive: "프리미티브",
    outcomeReady: "준비됨",

    visualResidual: "시각 잔차",
    factStrict: "엄격",
    factFloor: "하한",
    factLimit: "한계",
    factResidual: "잔차",
    factPage: "쪽",
    pageOrdinal: (page) => `${page}쪽`,
    ratioPerPage: (total, perPage) => `${total} / 쪽당 ${perPage}`,

    artifactService: "산출물 서비스",
    factArtifacts: "산출물",
    factFormats: "형식",
    factRoute: "경로",
    missing: "없음",

    chartSemantics: "차트 의미",
    factCharts: "차트",
    factFormula: "수식",
    factBehavior: "동작",
    factNative: "네이티브",
    factPreview: "미리보기",
    factSidecar: "사이드카",
    factPolicy: "정책",
    factPath: "경로",
    factSeries: "계열",
    seriesFormula: (linked, total) => `${linked}/${total} 수식`,
    factAction: "동작",
    factCatAxis: "범주 축",
    factValAxis: "값 축",
    factLegend: "범례",
    factCategory: "범주",
    factValues: "값",
    factFormat: "형식",
    factAxis: "축",
    legendHidden: "숨김",
    legendOverlaySuffix: " 겹침",
    none: "없음",
    univerHelperPreview: "Univer 도우미 미리보기",
    factAdapter: "어댑터",
    factPackage: "패키지",
    factChart: "차트",
    factBindings: "바인딩",
    factReadbacks: "역판독",

    formulaOracle: "수식 오라클",
    factCases: "사례",
    factEngine: "엔진",
    factDeps: "의존",
    factGraph: "그래프",
    factSteps: "단계",
    factSource: "원본",
    factInputs: "입력",
    factNamed: "이름 참조",
    factBinding: "바인딩",
    factMapping: "매핑",
    run: "실행",
    running: "실행 중",

    nativeArtifact: "네이티브 산출물",
    download: "내려받기",
    checkTrail: "점검 이력",
    open: "열기",

    semanticEditRoundtrip: "의미 편집 왕복",
    factOperation: "작업",
    factExpected: "기대값",
    factReplacements: "치환 수",
    replacementText: "치환 문구",
    apply: "적용",

    sampleCase: {
      "report-total-sum": "보고서 합계",
      "report-change-rate": "증감률",
      "actionflow-checkpoint": "ActionFlow 체크포인트",
    },
    readinessLanes: "준비 항목",
    exportHistory: "내보내기 이력",
    exportBlocked: "내보내기 차단됨",
    recheck: "다시 점검",
    exportFormat: (format) => `${format} 내보내기`,
  },
  model: {
    fallbackTitle: "문서 보고서",
    pageLabel: (pageIndex) => `${pageIndex}쪽`,
    pageDetailNotAttached: "쪽 단위 레이아웃 근거가 아직 연결되지 않았어요.",
    countNodes: (count) => `노드 ${count}개`,
    countParagraphs: (count) => `문단 ${count}개`,
    countTables: (count) => `표 ${count}개`,
    countPictures: (count) => `그림 ${count}개`,
    countLayers: (count) => `레이어 ${count}개`,

    evidenceNativeArtifact: (format) => `${format} 산출물`,
    evidenceFileCheck: "파일 점검",
    evidenceChartCheck: "차트 점검",
    evidenceComponentDetail: "구성요소 상세",
    evidenceVisualResidual: "시각 잔차",
    evidenceContentCheck: "내용 점검",
    detailServiceArtifact: "서비스 산출물",
    detailCheckPassed: "점검 통과",
    detailCheckBlocked: "점검 차단",
    detailChartProof: "차트 증거",
    detailNativeSemanticPass: "네이티브 의미 통과",
    detailMissing: "없음",

    laneProductionInput: "실사용 입력",
    laneSourceCorpus: "원본 말뭉치",
    laneProductionExport: "실사용 내보내기",
    laneSemanticRoundtrip: "의미 왕복",
    laneArtifactService: "산출물 서비스",
    laneFormulaOracle: "수식 오라클",
    laneChartSemantics: "차트 의미",
    lanePublishReadiness: "배포 준비",

    laneDetailAnalyzedBy: (fileName, jobId) => `${fileName} · 분석 ${jobId}`,
    laneDetailSourceLoaded: (sourceKind) =>
      `${sourceKind} 원본 모델을 불러왔어요`,
    laneDetailSourceMissing: "연결된 문서 원본 말뭉치 근거가 없어요",
    laneDetailTargetsReady: (ready, total) => `대상 ${ready}/${total} 준비됨`,
    laneDetailSemanticPass:
      "Coogo JSON 의미 편집이 네이티브 산출물에서 다시 읽혀요",
    laneDetailSemanticFail:
      "의미 편집에 대한 내용 점검이 아직 완결되지 않았어요",
    laneDetailServiceMissing:
      "네이티브 산출물 서비스 매니페스트가 연결되지 않았어요",
    laneDetailArtifactsPass: (pass, total) =>
      `네이티브 산출물 ${pass}/${total} 통과`,
    laneDetailCases: (pass, total, engine) =>
      `사례 ${pass}/${total} · ${engine}`,
    laneDetailCharts: (charts, series, mode) =>
      `차트/계열 ${charts}/${series} · ${mode}`,
    laneDetailNoBlockers: "보고서 내보내기 차단 사유 없음",
    laneDetailBlocked: "차단됨",

    blockerChartNoSeriesBindings:
      "차트 데이터에 차트는 있지만 계열 바인딩이 없어요.",
    blockerChartDataMissing: "차트 데이터가 없어요.",
    blockerChartDataMissingFormulaOnly:
      "차트 데이터가 없어 수식 차트 계열 참조만 사용해요.",
    blockerChartDataNotLoaded:
      "차트 데이터가 참조되어 있지만 아직 불러오지 못했어요.",
    blockerFixturePromotion:
      "픽스처/PoC 보고서 근거는 상용 업로드 준비 상태로 승격할 수 없어요.",
    blockerUploadIdMissing: "Studio 업로드 식별자가 없어요.",
    blockerAnalysisJobIdMissing: "플랫폼 문서 분석 작업 식별자가 없어요.",
    blockerAnalysisNotReady: "플랫폼 문서 분석이 아직 준비되지 않았어요.",
    blockerNormalizedNotReady:
      "정규화된 Coogo JSON과 저장된 근거가 아직 준비되지 않았어요.",
    blockerCommercialRequiresUpload:
      "상용 보고서 준비 상태에는 Studio 업로드와 플랫폼 분석 근거가 필요해요.",
    blockerUploadAndAnalysisMissing:
      "보고서 업로드와 플랫폼 분석 근거가 없어요.",
    blockerExportEvidenceMissing: "문서/보고서 내보내기 근거가 없어요.",
    blockerProductionGateNotReady:
      "문서 내보내기 실사용 관문이 아직 준비되지 않았어요.",
    blockerSemanticProofNotReady:
      "의미 편집 왕복 증거가 아직 준비되지 않았어요.",
    blockerArtifactServiceNotReady:
      "문서 내보내기 산출물 서비스가 아직 준비되지 않았어요.",
    blockerFormulaOracleNotReady:
      "스프레드시트 수식 오라클이 아직 준비되지 않았어요.",
    blockerTargetExportMissing: (format) => `${format} 내보내기 근거가 없어요.`,
    blockerTargetVisualMissing: (format) =>
      `${format} 시각 품질 근거가 없어요.`,
    blockerTargetNativeDetailMissing: (format) =>
      `${format} 네이티브 상세 근거가 없어요.`,
  },
};

const JA: ReportWorkspaceCopy = {
  surface: {
    workspaceEyebrow: "レポートワークスペース",
    reviewReport: "レポートを確認",
    paneSwitcherLabel: "レポートワークスペースのペイン",
    navigate: "ナビゲート",
    preview: "プレビュー",
    navigatorLabel: "レポートナビゲーター",
    pagesCount: (count) => `${count}ページ`,
    nodesCount: (count) => `ノード${count}件`,
    seriesCount: (count) => `系列${count}件`,

    a4Portrait: "A4 縦",
    coogoReport: "Coogo レポート",
    pageOfPages: (label, total) => `${label} / 全${total}ページ`,
    noPages: "ページなし",
    laneSource: "ソース",
    lanePreview: "プレビュー",
    laneExport: "エクスポート",
    pageEvidence: "ページの根拠",
    attachPageEvidence:
      "ページ単位で確認するには、ドキュメントページの根拠を接続してください。",
    noPageEvidenceTitle: "レポートページの根拠がありません",
    noPageEvidenceBody:
      "プレビューとエクスポートの準備状況を評価する前に、対応するソースをアップロードまたは接続してください。",

    metricPages: "ページ",
    metricNodes: "ノード",
    metricReady: "準備完了",
    notAReportProject:
      "このプロジェクトにはレポートの根拠が接続されていません。",

    productionInput: "本番入力",
    factMode: "モード",
    factUpload: "アップロード",
    factFile: "ファイル",
    factAnalysis: "解析",
    factStatus: "状態",
    factAppSource: "アプリソース",
    factCheck: "チェック",
    upload: "アップロード",
    uploading: "アップロード中",
    uploadAttached: "アップロードの根拠を接続しました。",
    uploadPending: "プラットフォーム解析を実行中です。",
    uploadFailed: "レポートのアップロードに失敗しました。",
    sourceNotReady:
      "このソースは編集可能な Coogo JSON やネイティブ書き出しの準備ができていません。",

    documentPages: "ドキュメントページ",
    factSelected: "選択中",
    factDetail: "詳細",
    factEvidence: "根拠",
    reviewPage: "ページを確認",
    editPage: "ページを編集",
    validatePage: "ページを検証",
    exportPage: "ページを書き出し",
    attachPagesOrGraph:
      "ページ単位で確認するには、ドキュメントモデルのページかレイアウトグラフを接続してください。",

    exportTargets: "書き出し対象",
    targetReadiness: (format) => `${format} の準備状況`,
    validateTarget: "対象を検証",
    checkWriter: "ライター",
    checkReadback: "読み戻し",
    checkRender: "レンダリング",
    checkPages: "ページ数",
    checkParity: "同等性",
    checkSemantic: "セマンティクス",
    outcomePass: "合格",
    outcomeBlocked: "ブロック",
    outcomeFail: "不合格",
    outcomePending: "保留",
    factOracle: "オラクル",
    factWriterState: "ライター状態",
    factArtifact: "成果物",
    factSize: "サイズ",
    evidencePacket: "根拠パケット",
    notRecorded: "記録なし",

    nativeDetail: "ネイティブ詳細",
    factComponents: "コンポーネント",
    factCoverage: "カバレッジ",
    factRules: "ルール",
    factProfiles: "プロファイル",
    factBorders: "罫線",
    factAuthority: "権威",
    factPrimitive: "プリミティブ",
    outcomeReady: "準備完了",

    visualResidual: "視覚残差",
    factStrict: "厳密",
    factFloor: "下限",
    factLimit: "上限",
    factResidual: "残差",
    factPage: "ページ",
    pageOrdinal: (page) => `${page}ページ`,
    ratioPerPage: (total, perPage) => `${total} / ページあたり ${perPage}`,

    artifactService: "成果物サービス",
    factArtifacts: "成果物",
    factFormats: "形式",
    factRoute: "ルート",
    missing: "なし",

    chartSemantics: "チャートセマンティクス",
    factCharts: "チャート",
    factFormula: "数式",
    factBehavior: "挙動",
    factNative: "ネイティブ",
    factPreview: "プレビュー",
    factSidecar: "サイドカー",
    factPolicy: "ポリシー",
    factPath: "パス",
    factSeries: "系列",
    seriesFormula: (linked, total) => `${linked}/${total} 数式`,
    factAction: "アクション",
    factCatAxis: "分類軸",
    factValAxis: "数値軸",
    factLegend: "凡例",
    factCategory: "分類",
    factValues: "値",
    factFormat: "書式",
    factAxis: "軸",
    legendHidden: "非表示",
    legendOverlaySuffix: " 重ね",
    none: "なし",
    univerHelperPreview: "Univer ヘルパープレビュー",
    factAdapter: "アダプター",
    factPackage: "パッケージ",
    factChart: "チャート",
    factBindings: "バインディング",
    factReadbacks: "読み戻し",

    formulaOracle: "数式オラクル",
    factCases: "ケース",
    factEngine: "エンジン",
    factDeps: "依存",
    factGraph: "グラフ",
    factSteps: "ステップ",
    factSource: "ソース",
    factInputs: "入力",
    factNamed: "名前付き参照",
    factBinding: "バインディング",
    factMapping: "マッピング",
    run: "実行",
    running: "実行中",

    nativeArtifact: "ネイティブ成果物",
    download: "ダウンロード",
    checkTrail: "チェック履歴",
    open: "開く",

    semanticEditRoundtrip: "セマンティック編集の往復",
    factOperation: "操作",
    factExpected: "期待値",
    factReplacements: "置換数",
    replacementText: "置換テキスト",
    apply: "適用",

    sampleCase: {
      "report-total-sum": "レポート合計",
      "report-change-rate": "変化率",
      "actionflow-checkpoint": "ActionFlow チェックポイント",
    },
    readinessLanes: "準備レーン",
    exportHistory: "書き出し履歴",
    exportBlocked: "書き出しがブロックされています",
    recheck: "再チェック",
    exportFormat: (format) => `${format} を書き出し`,
  },
  model: {
    fallbackTitle: "ドキュメントレポート",
    pageLabel: (pageIndex) => `${pageIndex}ページ`,
    pageDetailNotAttached:
      "ページ単位のレイアウト根拠はまだ接続されていません。",
    countNodes: (count) => `ノード${count}件`,
    countParagraphs: (count) => `段落${count}件`,
    countTables: (count) => `表${count}件`,
    countPictures: (count) => `画像${count}件`,
    countLayers: (count) => `レイヤー${count}件`,

    evidenceNativeArtifact: (format) => `${format} 成果物`,
    evidenceFileCheck: "ファイルチェック",
    evidenceChartCheck: "チャートチェック",
    evidenceComponentDetail: "コンポーネント詳細",
    evidenceVisualResidual: "視覚残差",
    evidenceContentCheck: "内容チェック",
    detailServiceArtifact: "サービス成果物",
    detailCheckPassed: "チェック合格",
    detailCheckBlocked: "チェックブロック",
    detailChartProof: "チャート証跡",
    detailNativeSemanticPass: "ネイティブセマンティクス合格",
    detailMissing: "なし",

    laneProductionInput: "本番入力",
    laneSourceCorpus: "ソースコーパス",
    laneProductionExport: "本番書き出し",
    laneSemanticRoundtrip: "セマンティック往復",
    laneArtifactService: "成果物サービス",
    laneFormulaOracle: "数式オラクル",
    laneChartSemantics: "チャートセマンティクス",
    lanePublishReadiness: "公開準備",

    laneDetailAnalyzedBy: (fileName, jobId) => `${fileName} · 解析 ${jobId}`,
    laneDetailSourceLoaded: (sourceKind) =>
      `${sourceKind} のソースモデルを読み込みました`,
    laneDetailSourceMissing:
      "接続されたドキュメントソースコーパスの根拠がありません",
    laneDetailTargetsReady: (ready, total) => `対象 ${ready}/${total} 準備完了`,
    laneDetailSemanticPass:
      "Coogo JSON のセマンティック編集がネイティブ成果物から読み戻せます",
    laneDetailSemanticFail: "セマンティック編集の内容チェックが未完了です",
    laneDetailServiceMissing:
      "ネイティブ成果物サービスのマニフェストが接続されていません",
    laneDetailArtifactsPass: (pass, total) =>
      `ネイティブ成果物 ${pass}/${total} 合格`,
    laneDetailCases: (pass, total, engine) =>
      `ケース ${pass}/${total} · ${engine}`,
    laneDetailCharts: (charts, series, mode) =>
      `チャート/系列 ${charts}/${series} · ${mode}`,
    laneDetailNoBlockers: "レポート書き出しのブロック要因なし",
    laneDetailBlocked: "ブロック",

    blockerChartNoSeriesBindings:
      "チャートデータにチャートはありますが系列バインディングがありません。",
    blockerChartDataMissing: "チャートデータがありません。",
    blockerChartDataMissingFormulaOnly:
      "チャートデータがないため、数式チャートの系列参照のみを使用します。",
    blockerChartDataNotLoaded:
      "チャートデータは参照されていますが、まだ読み込まれていません。",
    blockerFixturePromotion:
      "フィクスチャ/PoC のレポート根拠は商用アップロード準備として昇格できません。",
    blockerUploadIdMissing: "Studio のアップロード ID がありません。",
    blockerAnalysisJobIdMissing:
      "プラットフォーム文書解析のジョブ ID がありません。",
    blockerAnalysisNotReady: "プラットフォーム文書解析の準備ができていません。",
    blockerNormalizedNotReady:
      "正規化された Coogo JSON と保存済みの根拠が準備できていません。",
    blockerCommercialRequiresUpload:
      "商用レポートの準備には Studio のアップロードとプラットフォーム解析の根拠が必要です。",
    blockerUploadAndAnalysisMissing:
      "レポートのアップロードとプラットフォーム解析の根拠がありません。",
    blockerExportEvidenceMissing:
      "ドキュメント/レポート書き出しの根拠がありません。",
    blockerProductionGateNotReady:
      "ドキュメント書き出しの本番ゲートが準備できていません。",
    blockerSemanticProofNotReady:
      "セマンティック編集の往復証跡が準備できていません。",
    blockerArtifactServiceNotReady:
      "ドキュメント書き出しの成果物サービスが準備できていません。",
    blockerFormulaOracleNotReady:
      "スプレッドシートの数式オラクルが準備できていません。",
    blockerTargetExportMissing: (format) =>
      `${format} の書き出し根拠がありません。`,
    blockerTargetVisualMissing: (format) =>
      `${format} の視覚品質の根拠がありません。`,
    blockerTargetNativeDetailMissing: (format) =>
      `${format} のネイティブ詳細の根拠がありません。`,
  },
};

const ZH: ReportWorkspaceCopy = {
  surface: {
    workspaceEyebrow: "报告工作区",
    reviewReport: "检查报告",
    paneSwitcherLabel: "报告工作区面板",
    navigate: "导航",
    preview: "预览",
    navigatorLabel: "报告导航器",
    pagesCount: (count) => `${count} 页`,
    nodesCount: (count) => `${count} 个节点`,
    seriesCount: (count) => `${count} 个系列`,

    a4Portrait: "A4 纵向",
    coogoReport: "Coogo 报告",
    pageOfPages: (label, total) => `${label} / 共 ${total} 页`,
    noPages: "无页面",
    laneSource: "来源",
    lanePreview: "预览",
    laneExport: "导出",
    pageEvidence: "页面证据",
    attachPageEvidence: "请附加文档页面证据，以便按页检查此报告。",
    noPageEvidenceTitle: "没有报告页面证据",
    noPageEvidenceBody:
      "请先上传或附加受支持的来源，然后才能评估预览与导出的就绪状态。",

    metricPages: "页数",
    metricNodes: "节点",
    metricReady: "就绪",
    notAReportProject: "此项目未附加报告证据。",

    productionInput: "生产输入",
    factMode: "模式",
    factUpload: "上传",
    factFile: "文件",
    factAnalysis: "分析",
    factStatus: "状态",
    factAppSource: "应用来源",
    factCheck: "检查",
    upload: "上传",
    uploading: "上传中",
    uploadAttached: "已附加上传证据。",
    uploadPending: "平台分析进行中。",
    uploadFailed: "报告上传失败。",
    sourceNotReady: "该来源尚未准备好生成可编辑的 Coogo JSON 或原生导出。",

    documentPages: "文档页面",
    factSelected: "已选择",
    factDetail: "详情",
    factEvidence: "证据",
    reviewPage: "检查页面",
    editPage: "编辑页面",
    validatePage: "验证页面",
    exportPage: "导出页面",
    attachPagesOrGraph: "请附加文档模型页面或布局图，以便按页检查此报告。",

    exportTargets: "导出目标",
    targetReadiness: (format) => `${format} 就绪状态`,
    validateTarget: "验证目标",
    checkWriter: "写入器",
    checkReadback: "回读",
    checkRender: "渲染",
    checkPages: "页数",
    checkParity: "一致性",
    checkSemantic: "语义",
    outcomePass: "通过",
    outcomeBlocked: "受阻",
    outcomeFail: "失败",
    outcomePending: "待定",
    factOracle: "校验器",
    factWriterState: "写入器状态",
    factArtifact: "产物",
    factSize: "大小",
    evidencePacket: "证据包",
    notRecorded: "未记录",

    nativeDetail: "原生细节",
    factComponents: "组件",
    factCoverage: "覆盖率",
    factRules: "规则",
    factProfiles: "配置",
    factBorders: "边框",
    factAuthority: "权威",
    factPrimitive: "基元",
    outcomeReady: "就绪",

    visualResidual: "视觉残差",
    factStrict: "严格",
    factFloor: "下限",
    factLimit: "上限",
    factResidual: "残差",
    factPage: "页面",
    pageOrdinal: (page) => `第 ${page} 页`,
    ratioPerPage: (total, perPage) => `${total} / 每页 ${perPage}`,

    artifactService: "产物服务",
    factArtifacts: "产物",
    factFormats: "格式",
    factRoute: "路由",
    missing: "缺失",

    chartSemantics: "图表语义",
    factCharts: "图表",
    factFormula: "公式",
    factBehavior: "行为",
    factNative: "原生",
    factPreview: "预览",
    factSidecar: "边车",
    factPolicy: "策略",
    factPath: "路径",
    factSeries: "系列",
    seriesFormula: (linked, total) => `${linked}/${total} 公式`,
    factAction: "动作",
    factCatAxis: "分类轴",
    factValAxis: "数值轴",
    factLegend: "图例",
    factCategory: "分类",
    factValues: "值",
    factFormat: "格式",
    factAxis: "轴",
    legendHidden: "隐藏",
    legendOverlaySuffix: " 叠加",
    none: "无",
    univerHelperPreview: "Univer 助手预览",
    factAdapter: "适配器",
    factPackage: "包",
    factChart: "图表",
    factBindings: "绑定",
    factReadbacks: "回读",

    formulaOracle: "公式校验器",
    factCases: "用例",
    factEngine: "引擎",
    factDeps: "依赖",
    factGraph: "图",
    factSteps: "步骤",
    factSource: "来源",
    factInputs: "输入",
    factNamed: "命名引用",
    factBinding: "绑定",
    factMapping: "映射",
    run: "运行",
    running: "运行中",

    nativeArtifact: "原生产物",
    download: "下载",
    checkTrail: "检查记录",
    open: "打开",

    semanticEditRoundtrip: "语义编辑往返",
    factOperation: "操作",
    factExpected: "期望值",
    factReplacements: "替换数",
    replacementText: "替换文本",
    apply: "应用",

    sampleCase: {
      "report-total-sum": "报告合计",
      "report-change-rate": "变化率",
      "actionflow-checkpoint": "ActionFlow 检查点",
    },
    readinessLanes: "就绪项",
    exportHistory: "导出历史",
    exportBlocked: "导出受阻",
    recheck: "重新检查",
    exportFormat: (format) => `导出 ${format}`,
  },
  model: {
    fallbackTitle: "文档报告",
    pageLabel: (pageIndex) => `第 ${pageIndex} 页`,
    pageDetailNotAttached: "尚未附加页面级布局证据。",
    countNodes: (count) => `${count} 个节点`,
    countParagraphs: (count) => `${count} 个段落`,
    countTables: (count) => `${count} 个表格`,
    countPictures: (count) => `${count} 张图片`,
    countLayers: (count) => `${count} 个图层`,

    evidenceNativeArtifact: (format) => `${format} 产物`,
    evidenceFileCheck: "文件检查",
    evidenceChartCheck: "图表检查",
    evidenceComponentDetail: "组件细节",
    evidenceVisualResidual: "视觉残差",
    evidenceContentCheck: "内容检查",
    detailServiceArtifact: "服务产物",
    detailCheckPassed: "检查通过",
    detailCheckBlocked: "检查受阻",
    detailChartProof: "图表凭据",
    detailNativeSemanticPass: "原生语义通过",
    detailMissing: "缺失",

    laneProductionInput: "生产输入",
    laneSourceCorpus: "来源语料",
    laneProductionExport: "生产导出",
    laneSemanticRoundtrip: "语义往返",
    laneArtifactService: "产物服务",
    laneFormulaOracle: "公式校验器",
    laneChartSemantics: "图表语义",
    lanePublishReadiness: "发布就绪",

    laneDetailAnalyzedBy: (fileName, jobId) => `${fileName} · 分析 ${jobId}`,
    laneDetailSourceLoaded: (sourceKind) => `已加载 ${sourceKind} 来源模型`,
    laneDetailSourceMissing: "未附加文档来源语料证据",
    laneDetailTargetsReady: (ready, total) => `目标 ${ready}/${total} 就绪`,
    laneDetailSemanticPass: "Coogo JSON 语义编辑可从原生产物回读",
    laneDetailSemanticFail: "语义编辑的内容检查尚未完成",
    laneDetailServiceMissing: "未附加原生产物服务清单",
    laneDetailArtifactsPass: (pass, total) => `原生产物 ${pass}/${total} 通过`,
    laneDetailCases: (pass, total, engine) =>
      `用例 ${pass}/${total} · ${engine}`,
    laneDetailCharts: (charts, series, mode) =>
      `图表/系列 ${charts}/${series} · ${mode}`,
    laneDetailNoBlockers: "没有报告导出阻塞项",
    laneDetailBlocked: "受阻",

    blockerChartNoSeriesBindings: "图表数据有图表但没有系列绑定。",
    blockerChartDataMissing: "缺少图表数据。",
    blockerChartDataMissingFormulaOnly:
      "缺少图表数据，仅使用公式图表系列引用。",
    blockerChartDataNotLoaded: "已引用图表数据，但尚未加载。",
    blockerFixturePromotion: "夹具/PoC 报告证据不能提升为商用上传就绪状态。",
    blockerUploadIdMissing: "缺少 Studio 上传标识。",
    blockerAnalysisJobIdMissing: "缺少平台文档分析作业标识。",
    blockerAnalysisNotReady: "平台文档分析尚未就绪。",
    blockerNormalizedNotReady: "规范化的 Coogo JSON 及其保存的证据尚未就绪。",
    blockerCommercialRequiresUpload:
      "商用报告就绪需要 Studio 上传与平台分析证据。",
    blockerUploadAndAnalysisMissing: "缺少报告上传与平台分析证据。",
    blockerExportEvidenceMissing: "缺少文档/报告导出证据。",
    blockerProductionGateNotReady: "文档导出生产关卡尚未就绪。",
    blockerSemanticProofNotReady: "语义编辑往返凭据尚未就绪。",
    blockerArtifactServiceNotReady: "文档导出产物服务尚未就绪。",
    blockerFormulaOracleNotReady: "电子表格公式校验器尚未就绪。",
    blockerTargetExportMissing: (format) => `缺少 ${format} 导出证据。`,
    blockerTargetVisualMissing: (format) => `缺少 ${format} 视觉质量证据。`,
    blockerTargetNativeDetailMissing: (format) =>
      `缺少 ${format} 原生细节证据。`,
  },
};

const ZH_TW: ReportWorkspaceCopy = {
  surface: {
    workspaceEyebrow: "報告工作區",
    reviewReport: "檢視報告",
    paneSwitcherLabel: "報告工作區面板",
    navigate: "導覽",
    preview: "預覽",
    navigatorLabel: "報告導覽器",
    pagesCount: (count) => `${count} 頁`,
    nodesCount: (count) => `${count} 個節點`,
    seriesCount: (count) => `${count} 個數列`,

    a4Portrait: "A4 直向",
    coogoReport: "Coogo 報告",
    pageOfPages: (label, total) => `${label} / 共 ${total} 頁`,
    noPages: "無頁面",
    laneSource: "來源",
    lanePreview: "預覽",
    laneExport: "匯出",
    pageEvidence: "頁面佐證",
    attachPageEvidence: "請連結文件頁面佐證，才能逐頁檢視這份報告。",
    noPageEvidenceTitle: "沒有報告頁面佐證",
    noPageEvidenceBody:
      "請先上傳或連結支援的來源，才能評估預覽與匯出的就緒狀態。",

    metricPages: "頁數",
    metricNodes: "節點",
    metricReady: "就緒",
    notAReportProject: "這個專案尚未連結報告佐證。",

    productionInput: "正式輸入",
    factMode: "模式",
    factUpload: "上傳",
    factFile: "檔案",
    factAnalysis: "分析",
    factStatus: "狀態",
    factAppSource: "應用來源",
    factCheck: "檢查",
    upload: "上傳",
    uploading: "上傳中",
    uploadAttached: "已連結上傳佐證。",
    uploadPending: "平台分析進行中。",
    uploadFailed: "報告上傳失敗。",
    sourceNotReady: "此來源尚未準備好產生可編輯的 Coogo JSON 或原生匯出。",

    documentPages: "文件頁面",
    factSelected: "已選取",
    factDetail: "詳細",
    factEvidence: "佐證",
    reviewPage: "檢視頁面",
    editPage: "編輯頁面",
    validatePage: "驗證頁面",
    exportPage: "匯出頁面",
    attachPagesOrGraph: "請連結文件模型頁面或版面圖，才能逐頁檢視這份報告。",

    exportTargets: "匯出目標",
    targetReadiness: (format) => `${format} 就緒狀態`,
    validateTarget: "驗證目標",
    checkWriter: "寫入器",
    checkReadback: "回讀",
    checkRender: "算繪",
    checkPages: "頁數",
    checkParity: "一致性",
    checkSemantic: "語意",
    outcomePass: "通過",
    outcomeBlocked: "受阻",
    outcomeFail: "失敗",
    outcomePending: "待處理",
    factOracle: "校驗器",
    factWriterState: "寫入器狀態",
    factArtifact: "產出物",
    factSize: "大小",
    evidencePacket: "佐證封包",
    notRecorded: "未記錄",

    nativeDetail: "原生細節",
    factComponents: "元件",
    factCoverage: "涵蓋率",
    factRules: "規則",
    factProfiles: "設定檔",
    factBorders: "框線",
    factAuthority: "權威",
    factPrimitive: "基元",
    outcomeReady: "就緒",

    visualResidual: "視覺殘差",
    factStrict: "嚴格",
    factFloor: "下限",
    factLimit: "上限",
    factResidual: "殘差",
    factPage: "頁面",
    pageOrdinal: (page) => `第 ${page} 頁`,
    ratioPerPage: (total, perPage) => `${total} / 每頁 ${perPage}`,

    artifactService: "產出物服務",
    factArtifacts: "產出物",
    factFormats: "格式",
    factRoute: "路由",
    missing: "缺少",

    chartSemantics: "圖表語意",
    factCharts: "圖表",
    factFormula: "公式",
    factBehavior: "行為",
    factNative: "原生",
    factPreview: "預覽",
    factSidecar: "附掛",
    factPolicy: "政策",
    factPath: "路徑",
    factSeries: "數列",
    seriesFormula: (linked, total) => `${linked}/${total} 公式`,
    factAction: "動作",
    factCatAxis: "類別軸",
    factValAxis: "數值軸",
    factLegend: "圖例",
    factCategory: "類別",
    factValues: "值",
    factFormat: "格式",
    factAxis: "軸",
    legendHidden: "隱藏",
    legendOverlaySuffix: " 疊加",
    none: "無",
    univerHelperPreview: "Univer 輔助預覽",
    factAdapter: "轉接器",
    factPackage: "套件",
    factChart: "圖表",
    factBindings: "繫結",
    factReadbacks: "回讀",

    formulaOracle: "公式校驗器",
    factCases: "案例",
    factEngine: "引擎",
    factDeps: "相依",
    factGraph: "圖",
    factSteps: "步驟",
    factSource: "來源",
    factInputs: "輸入",
    factNamed: "具名參照",
    factBinding: "繫結",
    factMapping: "對應",
    run: "執行",
    running: "執行中",

    nativeArtifact: "原生產出物",
    download: "下載",
    checkTrail: "檢查紀錄",
    open: "開啟",

    semanticEditRoundtrip: "語意編輯往返",
    factOperation: "操作",
    factExpected: "預期值",
    factReplacements: "取代次數",
    replacementText: "取代文字",
    apply: "套用",

    sampleCase: {
      "report-total-sum": "報告合計",
      "report-change-rate": "變化率",
      "actionflow-checkpoint": "ActionFlow 檢查點",
    },
    readinessLanes: "就緒項目",
    exportHistory: "匯出紀錄",
    exportBlocked: "匯出受阻",
    recheck: "重新檢查",
    exportFormat: (format) => `匯出 ${format}`,
  },
  model: {
    fallbackTitle: "文件報告",
    pageLabel: (pageIndex) => `第 ${pageIndex} 頁`,
    pageDetailNotAttached: "尚未連結頁面層級的版面佐證。",
    countNodes: (count) => `${count} 個節點`,
    countParagraphs: (count) => `${count} 個段落`,
    countTables: (count) => `${count} 個表格`,
    countPictures: (count) => `${count} 張圖片`,
    countLayers: (count) => `${count} 個圖層`,

    evidenceNativeArtifact: (format) => `${format} 產出物`,
    evidenceFileCheck: "檔案檢查",
    evidenceChartCheck: "圖表檢查",
    evidenceComponentDetail: "元件細節",
    evidenceVisualResidual: "視覺殘差",
    evidenceContentCheck: "內容檢查",
    detailServiceArtifact: "服務產出物",
    detailCheckPassed: "檢查通過",
    detailCheckBlocked: "檢查受阻",
    detailChartProof: "圖表憑證",
    detailNativeSemanticPass: "原生語意通過",
    detailMissing: "缺少",

    laneProductionInput: "正式輸入",
    laneSourceCorpus: "來源語料",
    laneProductionExport: "正式匯出",
    laneSemanticRoundtrip: "語意往返",
    laneArtifactService: "產出物服務",
    laneFormulaOracle: "公式校驗器",
    laneChartSemantics: "圖表語意",
    lanePublishReadiness: "發布就緒",

    laneDetailAnalyzedBy: (fileName, jobId) => `${fileName} · 分析 ${jobId}`,
    laneDetailSourceLoaded: (sourceKind) => `已載入 ${sourceKind} 來源模型`,
    laneDetailSourceMissing: "未連結文件來源語料佐證",
    laneDetailTargetsReady: (ready, total) => `目標 ${ready}/${total} 就緒`,
    laneDetailSemanticPass: "Coogo JSON 語意編輯可從原生產出物回讀",
    laneDetailSemanticFail: "語意編輯的內容檢查尚未完成",
    laneDetailServiceMissing: "未連結原生產出物服務資訊清單",
    laneDetailArtifactsPass: (pass, total) =>
      `原生產出物 ${pass}/${total} 通過`,
    laneDetailCases: (pass, total, engine) =>
      `案例 ${pass}/${total} · ${engine}`,
    laneDetailCharts: (charts, series, mode) =>
      `圖表/數列 ${charts}/${series} · ${mode}`,
    laneDetailNoBlockers: "沒有報告匯出阻擋項",
    laneDetailBlocked: "受阻",

    blockerChartNoSeriesBindings: "圖表資料有圖表但沒有數列繫結。",
    blockerChartDataMissing: "缺少圖表資料。",
    blockerChartDataMissingFormulaOnly:
      "缺少圖表資料，僅使用公式圖表數列參照。",
    blockerChartDataNotLoaded: "已參照圖表資料，但尚未載入。",
    blockerFixturePromotion: "夾具/PoC 報告佐證無法升級為商用上傳就緒狀態。",
    blockerUploadIdMissing: "缺少 Studio 上傳識別碼。",
    blockerAnalysisJobIdMissing: "缺少平台文件分析工作識別碼。",
    blockerAnalysisNotReady: "平台文件分析尚未就緒。",
    blockerNormalizedNotReady: "正規化的 Coogo JSON 與其儲存佐證尚未就緒。",
    blockerCommercialRequiresUpload:
      "商用報告就緒需要 Studio 上傳與平台分析佐證。",
    blockerUploadAndAnalysisMissing: "缺少報告上傳與平台分析佐證。",
    blockerExportEvidenceMissing: "缺少文件/報告匯出佐證。",
    blockerProductionGateNotReady: "文件匯出正式關卡尚未就緒。",
    blockerSemanticProofNotReady: "語意編輯往返憑證尚未就緒。",
    blockerArtifactServiceNotReady: "文件匯出產出物服務尚未就緒。",
    blockerFormulaOracleNotReady: "試算表公式校驗器尚未就緒。",
    blockerTargetExportMissing: (format) => `缺少 ${format} 匯出佐證。`,
    blockerTargetVisualMissing: (format) => `缺少 ${format} 視覺品質佐證。`,
    blockerTargetNativeDetailMissing: (format) =>
      `缺少 ${format} 原生細節佐證。`,
  },
};

const COPY: Record<LocaleCode, ReportWorkspaceCopy> = {
  en: EN,
  ko: KO,
  ja: JA,
  zh: ZH,
  "zh-TW": ZH_TW,
};

export function getReportWorkspaceCopy(
  locale: LocaleCode | undefined,
): ReportWorkspaceCopy {
  return (locale && COPY[locale]) || COPY.en;
}

export function getReportWorkspaceModelCopy(
  locale: LocaleCode | undefined,
): ReportWorkspaceModelCopy {
  return getReportWorkspaceCopy(locale).model;
}
